import { createContext, useState, useCallback } from 'react';
import { filter, 
  getAllAvailable, 
  getById, 
  grab,
  cancel,
  submit } from '../api/withdrawalApi';
import { useSnackbar } from './SnackbarContext'; 
import { getErrorMessageByCode, formatString } from '../utils/utils';

export const WithdrawalContext = createContext();

export const WithdrawalProvider = ({ children }) => {
  const { showSnackbar } = useSnackbar();
  const [withdrawals, setWithdrawals] = useState([]);
  const [totalCount, setTotalCount] = useState(0);   
  const [loading, setLoading] = useState(true);
  const [selectedWithdrawalId, setSelectedWithdrawalId] = useState(null);

  const fetchWithdrawals = useCallback(async(
    statusIds = null, 
    date = null,
    searchOrderId = null,
    pageNumber = 1,
    pageSize = null,
    sortDirection = 'DESC'   
  ) => {
    setLoading(true);
    try{
      const response = await filter(statusIds, date, searchOrderId, pageSize, pageNumber, sortDirection);
      if(response.isSuccess && response.data){
          setWithdrawals(response.data.withdrawals);
          setTotalCount(response.data.totalCount); 
          return response.data.withdrawals;
      }
      else{
        const errorMessage = getErrorMessageByCode(response.errorCode);
        showSnackbar(errorMessage, 'error');
        return null;
      }
    }
    catch(error){
      console.log('fetchWithdrawals error');
      return null;
    }
    finally{
      setLoading(false);
    }
  }, [showSnackbar]);

  const fetchAvailableWithdrawals = useCallback(async() => {
    setLoading(true);
    const response = await getAllAvailable();
    if(response.isSuccess){ 
      const sortedData = [...response.data].sort(
        (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt)
      );
      setWithdrawals(sortedData);
    }
    else{
      const errorMessage = getErrorMessageByCode(response.errorCode);
      showSnackbar(errorMessage, 'error');
    }
    setLoading(false);
  }, [showSnackbar]);

  const fetchWithdrawalById = async (withdrawalId) => {
    setLoading(true);
    let data = null;
    const response = await getById(withdrawalId);
    if(response.isSuccess){
      data = response.data === '' ? null : response.data
    }
    else{
      const errorMessage = getErrorMessageByCode(response.errorCode);
      showSnackbar(errorMessage, 'error');
    }
    setLoading(false);
    return data;
  };

  const grabWithdrawal = async (withdrawal) => {
    let result = {
      isSuccess: false,
      errorCode: null
    };
    const response = await grab(withdrawal.id);
    if (response.isSuccess) {
      result.isSuccess = true;
    } else {
      const errorMessage = formatString(getErrorMessageByCode(response.errorCode), [withdrawal.orderId]);
      showSnackbar(errorMessage, 'error', null, true);
    }
    return result;
  };

  const cancelWithdrawal = async (withdrawalId) => {
    let result = {
      isSuccess: false,
      errorCode: null
    };
    const response = await cancel(withdrawalId);
    if (response.isSuccess) {
      result.isSuccess = true;
    } else {
      const errorMessage = getErrorMessageByCode(result.errorCode);
      showSnackbar(errorMessage, 'error', null, true);
    }
    setSelectedWithdrawalId(null);
    return result;
  };

  const submitWithdrawal = async (withdrawalId, bankAccountId, attachmentList) => {
    setLoading(true);
    let result = {
      isSuccess: false,
      errorCode: null
    };
    try {
      const response = await submit(withdrawalId, bankAccountId, attachmentList);
      if (response.isSuccess) {
        result.isSuccess = true;
      } else {
        const errorMessage = formatString(getErrorMessageByCode(response.errorCode), ["W"+ withdrawalId]);
        showSnackbar(errorMessage, 'error', null, true);
      }
    } catch (error) {
      console.error('Error submitting attachments:', error);
    } finally {
      setSelectedWithdrawalId(null);
      setLoading(false);
    }
    return result;
  };

  const getProcessingWithdrawalId = async () => {
    const response = await filter([8]);
    const withdrawalId = response.data?.withdrawals?.find(record => record != null)?.id;
    if(withdrawalId){
      setSelectedWithdrawalId(withdrawalId);
    } else {
      setSelectedWithdrawalId(null);
    }
    return withdrawalId;
  }

  const resetWithdrawals = () => {
    setWithdrawals([]);
    setTotalCount(0);
  };

  return (
    <WithdrawalContext.Provider
      value={{
        withdrawals,
        setWithdrawals,
        totalCount,   
        loading,
        setLoading,
        fetchWithdrawals,
        fetchAvailableWithdrawals,
        fetchWithdrawalById,
        grabWithdrawal,
        cancelWithdrawal,
        submitWithdrawal,
        selectedWithdrawalId, 
        setSelectedWithdrawalId,
        getProcessingWithdrawalId,
        resetWithdrawals
      }}
    >
      {children}
    </WithdrawalContext.Provider>
  );
};
